import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { billingApi, opsApi } from '@/api/ops';
import type { RetentionOverride, RetentionPolicy, RetentionPolicyUpdate } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Select } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { CheckboxField, ReasonField, reasonOk } from '@/components/ops/Forms';
import { LoadMore, PageHeader, Section } from '@/components/ops/Layout';
import { ConfirmDialog } from '@/components/ops/Modal';
import { EnvironmentPicker } from '@/components/ops/Pickers';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { RetentionPicker, retentionFloor, validateRetention } from '@/components/ops/RetentionPicker';
import { formatNumber, formatTimestamp, shortId } from '@/ops/format';
import { LIVE, REPLICA } from '@/ops/live';
import { useSelectedEnvironment } from '@/ops/useEnvironment';
import { TenantName } from '@/components/ops/TenantPicker';

const policiesKey = (env: string) => ['ops', 'billing', env, 'retention-policies'] as const;

/**
 * Retention (D-CP23, BL10): how long each data class lives, from a CLOSED value set (7 / 30 / 60 / 90 /
 * 365 days) so a typo can never delete a year of data; invoices and ledger rows never below 365. Purge
 * workers read the policy at run time and purge a class ONLY when it is enforced (R20 — null or absent is
 * not enforced); a change applies from the next run and never resurrects data.
 */
export function RetentionPage() {
  const { env, setEnv, environments } = useSelectedEnvironment();
  return (
    <RequirePermission permission={PERMISSIONS.billingRead} what="Retention">
      <PageHeader title="Retention" subtitle="Per data class, with per-tenant overrides. Live from billing."
        actions={<EnvironmentPicker env={env} environments={environments} onChange={setEnv} />} />
      {env && (
        <>
          <PoliciesSection env={env} />
          <OverridesSection env={env} />
          <RunsSection env={env} />
        </>
      )}
    </RequirePermission>
  );
}

/** Null or absent is NOT enforced (fail-safe, R20): only an explicit `true` lets a purge worker delete. */
export function isEnforced(policy: Pick<RetentionPolicy, 'enforced'>): boolean {
  return policy.enforced === true;
}

export interface RetentionChange {
  policy: RetentionPolicy;
  days: number;
  enforced: boolean;
  daysChanged: boolean;
  enforcedChanged: boolean;
}

/** The rows the admin actually changed (days and/or the enforcement switch), against the live policies. */
export function retentionChanges(
  policies: readonly RetentionPolicy[],
  draftDays: Readonly<Record<string, number>>,
  draftEnforced: Readonly<Record<string, boolean>>,
): RetentionChange[] {
  return policies.flatMap((policy) => {
    const days = draftDays[policy.data_class] ?? policy.default_days;
    const enforced = draftEnforced[policy.data_class] ?? isEnforced(policy);
    const daysChanged = days !== policy.default_days;
    const enforcedChanged = enforced !== isEnforced(policy);
    return daysChanged || enforcedChanged ? [{ policy, days, enforced, daysChanged, enforcedChanged }] : [];
  });
}

/** The PUT rows: days always (billing requires them), `enforced` only when the admin flipped it. */
export function toPolicyUpdates(changes: readonly RetentionChange[]): RetentionPolicyUpdate[] {
  return changes.map((c) => ({
    data_class: c.policy.data_class,
    default_days: c.days,
    ...(c.enforcedChanged ? { enforced: c.enforced } : {}),
  }));
}

/**
 * Classes billing never deletes: an invoice past its retention is ARCHIVED (the run records
 * `archived_not_purged` — LedgerRetentionJob, R13), never purged, whatever the policy says.
 */
export const ARCHIVED_NEVER_PURGED: ReadonlySet<string> = new Set(['invoices']);

/** Classes whose detail rows are rolled into monthly totals before the detail is deleted. */
const ROLLED_UP_FIRST: ReadonlySet<string> = new Set(['ledger']);

/** True when the next purge run deletes data the current policy keeps: enforcement turned on, or shortened while on. */
export function isDestructive(change: RetentionChange): boolean {
  if (ARCHIVED_NEVER_PURGED.has(change.policy.data_class)) return false;
  return change.enforced && (change.enforcedChanged || change.days < change.policy.default_days);
}

/** What saving this row does, in words — shown in the confirmation. */
export function changeEffect(change: RetentionChange): string {
  const { policy, days, enforced, daysChanged, enforcedChanged } = change;
  const dayText = daysChanged ? `${policy.default_days} → ${days} days. ` : '';
  if (ARCHIVED_NEVER_PURGED.has(policy.data_class)) {
    return `${dayText}Invoices are archived after ${days} days, never deleted.`;
  }
  const deleted = ROLLED_UP_FIRST.has(policy.data_class)
    ? `rolled into monthly totals, then the detail rows are deleted permanently`
    : `deleted permanently`;
  if (enforcedChanged && enforced) {
    return `${dayText}Enforcement ON: from the next purge run, data older than ${days} days is ${deleted}.`;
  }
  if (enforcedChanged) {
    return `${dayText}Enforcement OFF: purge runs skip this class from the next run. Nothing already purged comes back.`;
  }
  if (!enforced) return `${dayText}Not enforced — the new value applies only once the class is enforced.`;
  return days < policy.default_days
    ? `${dayText}Shorter — at the next purge run, data older than ${days} days is ${deleted}.`
    : `${dayText}Longer — kept longer from the next run. Nothing already purged comes back.`;
}

function PoliciesSection({ env }: { env: string }) {
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const policies = useQuery({ queryKey: policiesKey(env), queryFn: () => billingApi.retentionPolicies(env), ...LIVE });
  const [draftDays, setDraftDays] = useState<Record<string, number>>({});
  const [draftEnforced, setDraftEnforced] = useState<Record<string, boolean>>({});
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const changes = policies.data ? retentionChanges(policies.data.policies, draftDays, draftEnforced) : [];
  const invalid = policies.data
    ? changes.map((c) => validateRetention(c.days, policies.data!.allowed, retentionFloor(c.policy.data_class, c.policy.floor_days))).filter(Boolean)
    : [];
  const destructive = changes.filter(isDestructive);

  const discard = () => { setDraftDays({}); setDraftEnforced({}); };
  const save = useMutation({
    mutationFn: () => billingApi.updateRetentionPolicies(env, toPolicyUpdates(changes), reason.trim()),
    onSuccess: (updated) => {
      // The table re-renders from billing's answer, never from the draft: a flag billing did not store
      // (an older billing that ignores `enforced`) shows as not enforced instead of a false success.
      queryClient.setQueryData(policiesKey(env), updated);
      discard();
      setReason('');
      setConfirming(false);
      setAcknowledged(false);
    },
  });

  return (
    <Section title="Default policies" meta="closed set: 7 / 30 / 60 / 90 / 365 days">
      <div className="callout callout-info" role="note" style={{ marginBottom: 12 }}>
        <span className="callout-icon" aria-hidden="true">i</span>
        <div className="t-body">
          Purge workers delete data only for classes marked <strong>Enforced</strong>. A class with no setting is treated as
          not enforced. Classes that hold existing data ship not enforced — review the days, then enable each with a reason.
          Tenant overrides change the days only, never enforcement.
        </div>
      </div>
      <QueryState query={policies} service="billing">
        {(data) => (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Data class</th><th>Retention</th><th>Purges</th><th>Floor</th><th>Updated</th></tr></thead>
                <tbody>
                  {data.policies.map((p: RetentionPolicy) => {
                    const floor = retentionFloor(p.data_class, p.floor_days);
                    const value = draftDays[p.data_class] ?? p.default_days;
                    const enforced = draftEnforced[p.data_class] ?? isEnforced(p);
                    const changed = value !== p.default_days || enforced !== isEnforced(p);
                    return (
                      <tr key={p.data_class} className={changed ? 'row-changed' : undefined}>
                        <td><span className="mono">{p.data_class}</span>{p.description && <div className="t-caption muted">{p.description}</div>}</td>
                        <td>
                          <RetentionPicker dataClass={p.data_class} value={value} allowed={data.allowed} floorDays={p.floor_days}
                            disabled={!canManage} onChange={(days) => setDraftDays({ ...draftDays, [p.data_class]: days })} />
                        </td>
                        <td>
                          <label className="checkbox-row t-label nowrap">
                            <input type="checkbox" checked={enforced} disabled={!canManage}
                              aria-label={`Enforce purges for ${p.data_class}`}
                              onChange={(event) => setDraftEnforced({ ...draftEnforced, [p.data_class]: event.target.checked })} />
                            {enforced
                              ? <StatusChip status="enforced" tone="info" />
                              : <StatusChip status="not_enforced" tone="muted" title="Purge runs skip this class." />}
                          </label>
                          {p.enforced == null && <div className="t-caption muted">unset — not enforced</div>}
                        </td>
                        <td className="t-caption">{floor !== null ? `≥ ${floor} days (legal minimum)` : '—'}</td>
                        <td className="t-caption">{formatTimestamp(p.updated_at)}<div className="muted">{p.updated_by ?? ''}</div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {canManage && changes.length > 0 && (
              <div className="stack-sm" style={{ marginTop: 12 }}>
                <ReasonField value={reason} onChange={setReason} />
                {invalid.length > 0 && <span className="t-caption note-error">{invalid[0]}</span>}
                <div className="form-actions">
                  <button type="button" className="btn btn-ghost" onClick={discard}>Discard</button>
                  <button type="button" className="btn btn-primary" disabled={!reasonOk(reason) || invalid.length > 0} onClick={() => setConfirming(true)}>
                    Save {changes.length} change(s)
                  </button>
                </div>
              </div>
            )}
            {confirming && (
              <ConfirmDialog title="Change retention?" confirmLabel="Save retention" destructive pending={save.isPending}
                confirmDisabled={destructive.length > 0 && !acknowledged}
                onConfirm={() => save.mutate()} onCancel={() => { setConfirming(false); setAcknowledged(false); save.reset(); }}>
                <ul className="problem-list t-body">
                  {changes.map((c) => (
                    <li key={c.policy.data_class}>
                      <span className="mono">{c.policy.data_class}</span>: {isDestructive(c) ? <strong>{changeEffect(c)}</strong> : changeEffect(c)}
                    </li>
                  ))}
                </ul>
                {destructive.length > 0 && (
                  <CheckboxField checked={acknowledged} onChange={setAcknowledged}
                    label={`I understand the next purge run permanently deletes data in ${destructive.map((c) => c.policy.data_class).join(', ')}.`} />
                )}
                <MutationError error={save.error} service="billing" />
              </ConfirmDialog>
            )}
          </>
        )}
      </QueryState>
    </Section>
  );
}

function OverridesSection({ env }: { env: string }) {
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const [tenantId, setTenantId] = useState('');
  const tenants = useQuery({ queryKey: ['ops', 'env-tenants', env], queryFn: () => opsApi.tenants.list(env), enabled: hasPermission(PERMISSIONS.hostsRead), ...REPLICA });
  const policies = useQuery({ queryKey: policiesKey(env), queryFn: () => billingApi.retentionPolicies(env), ...LIVE });
  const overrides = useQuery({
    queryKey: ['ops', 'billing', env, 'retention-overrides', tenantId],
    queryFn: () => billingApi.retentionOverrides(env, tenantId),
    enabled: !!tenantId,
    ...LIVE,
  });
  const [dataClass, setDataClass] = useState('');
  const [days, setDays] = useState(90);
  const [reason, setReason] = useState('');
  const [removing, setRemoving] = useState<RetentionOverride | null>(null);
  const [removeReason, setRemoveReason] = useState('');

  const policy = policies.data?.policies.find((p) => p.data_class === dataClass);
  const floor = policy ? retentionFloor(policy.data_class, policy.floor_days) : null;
  const invalid = policy ? validateRetention(days, policies.data?.allowed, floor) : 'Choose a data class.';

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['ops', 'billing', env, 'retention-overrides', tenantId] });
  const set = useMutation({
    mutationFn: () => billingApi.setRetentionOverride(env, tenantId, dataClass, days, reason.trim()),
    onSuccess: () => { setReason(''); refresh(); },
  });
  const remove = useMutation({
    mutationFn: () => billingApi.deleteRetentionOverride(env, tenantId, removing!.data_class, removeReason.trim()),
    onSuccess: () => { setRemoving(null); setRemoveReason(''); refresh(); },
  });

  return (
    <Section title="Tenant overrides" meta="a tenant's own retention for one class (the floor still applies)">
      <div style={{ maxWidth: 420 }}>
        <Select label="Tenant" value={tenantId} onChange={setTenantId} placeholder="Choose a tenant on this environment"
          options={(tenants.data?.tenants ?? []).map((t) => ({ value: t.tenant_id, label: t.name ?? t.tenant_id, hint: shortId(t.tenant_id) }))} />
      </div>
      {tenantId && (
        <div className="stack" style={{ marginTop: 16 }}>
          <QueryState query={overrides} service="billing">
            {(data) => data.overrides.length === 0 ? (
              <div className="empty-state t-body">No overrides — this tenant follows the defaults.</div>
            ) : (
              <table className="table">
                <thead><tr><th>Data class</th><th className="num">Days</th><th>Reason</th><th>Updated</th>{canManage && <th aria-label="Actions" />}</tr></thead>
                <tbody>
                  {data.overrides.map((o) => (
                    <tr key={o.data_class}>
                      <td className="mono">{o.data_class}</td>
                      <td className="num">{o.days}</td>
                      <td className="t-caption">{o.reason ?? '—'}</td>
                      <td className="t-caption">{formatTimestamp(o.updated_at)}<div className="muted">{o.created_by ?? ''}</div></td>
                      {canManage && <td><button type="button" className="btn btn-ghost btn-sm is-danger" onClick={() => setRemoving(o)}>Remove</button></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </QueryState>

          {canManage && policies.data && (
            <div className="stack-sm">
              <h3 className="t-heading" style={{ margin: 0 }}>Set an override</h3>
              <div className="row-end">
                <div style={{ width: 260 }}>
                  <Select label="Data class" value={dataClass} placeholder="Choose"
                    onChange={(v) => {
                      setDataClass(v);
                      // Start from the class's own default — always a legal value for it.
                      const chosen = policies.data!.policies.find((p) => p.data_class === v);
                      if (chosen) setDays(chosen.default_days);
                    }}
                    options={policies.data.policies.map((p) => ({
                      value: p.data_class, label: p.data_class, hint: `default ${p.default_days}d · ${isEnforced(p) ? 'enforced' : 'not enforced'}`,
                    }))} />
                </div>
                {policy && <RetentionPicker dataClass={policy.data_class} value={days} onChange={setDays} allowed={policies.data.allowed} floorDays={policy.floor_days} />}
              </div>
              {policy && invalid && <span className="t-caption note-error">{invalid}</span>}
              {policy && !isEnforced(policy) && (
                <span className="t-caption muted">
                  {policy.data_class} is not enforced: the override is stored, and purges use it once the class is enforced.
                </span>
              )}
              <ReasonField value={reason} onChange={setReason} />
              <div className="form-actions">
                <button type="button" className="btn btn-primary" disabled={!!invalid || !reasonOk(reason) || set.isPending} onClick={() => set.mutate()}>
                  {set.isPending ? 'Saving…' : 'Set override'}
                </button>
              </div>
              <MutationError error={set.error} service="billing" />
            </div>
          )}
        </div>
      )}
      {removing && (
        <ConfirmDialog title={`Remove the ${removing.data_class} override?`} confirmLabel="Remove override" pending={remove.isPending}
          confirmDisabled={!reasonOk(removeReason)} onConfirm={() => remove.mutate()} onCancel={() => { setRemoving(null); remove.reset(); }}>
          <p className="t-body" style={{ margin: 0 }}>The tenant falls back to the default policy from the next purge run.</p>
          <ReasonField value={removeReason} onChange={setRemoveReason} />
          <MutationError error={remove.error} service="billing" />
        </ConfirmDialog>
      )}
    </Section>
  );
}

function RunsSection({ env }: { env: string }) {
  const [dataClass, setDataClass] = useState('');
  const policies = useQuery({ queryKey: policiesKey(env), queryFn: () => billingApi.retentionPolicies(env), ...LIVE });
  const runs = useInfiniteQuery({
    queryKey: ['ops', 'billing', env, 'retention-runs', dataClass],
    queryFn: ({ pageParam }) => billingApi.retentionRuns(env, { data_class: dataClass || undefined, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    ...LIVE,
  });
  const rows = runs.data?.pages.flatMap((p) => p.runs) ?? [];

  return (
    <Section title="Last purge runs" meta="every purge rolls up first and records itself"
      actions={<Select label="Class" inline value={dataClass} onChange={setDataClass} placeholder="All classes"
        options={(policies.data?.policies ?? []).map((p) => ({ value: p.data_class, label: p.data_class }))} />} flush>
      <QueryState query={{ ...runs, data: runs.data ? rows : undefined }} service="billing">
        {(list) => list.length === 0 ? (
          <div className="empty-state t-body">No purge runs recorded.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Ran</th><th>Service · class</th><th>Tenant</th><th className="num">Purged</th><th className="num">Rolled up</th><th className="num">Days</th><th>Status</th></tr></thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id}>
                    <td className="t-caption">{formatTimestamp(r.ran_at)}</td>
                    <td className="mono t-caption">{r.service} · {r.data_class}</td>
                    <td className="t-caption">{r.tenant_id ? <TenantName env={env} id={r.tenant_id} /> : 'all tenants'}</td>
                    <td className="num">{formatNumber(r.rows_purged)}</td>
                    <td className="num">{formatNumber(r.rows_rolled_up)}</td>
                    <td className="num">{r.days_applied ?? '—'}</td>
                    <td><StatusChip status={r.status} />{r.note && <div className="t-caption muted">{r.note}</div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>
      <LoadMore hasMore={!!runs.hasNextPage} loading={runs.isFetchingNextPage} onLoad={() => runs.fetchNextPage()} />
    </Section>
  );
}
